import axios from 'axios';

const createApi = async () => {
  let baseURL;

  // Check if we're in a Nuxt environment
  if (process.client && window?.__NUXT__) {
    try {
      // Dynamic import for Nuxt environment
      const nuxtConfig = window.__NUXT__?.config?.public?.apiBaseUrl;
      baseURL = nuxtConfig || import.meta.env.VITE_API_BASE_URL;
    } catch (error) {
      console.warn('Failed to load Nuxt runtime config:', error);
      baseURL = import.meta.env.VITE_API_BASE_URL;
    }
  } else {
    // For Vite/non-Nuxt environment
    baseURL = import.meta.env.VITE_API_BASE_URL;
  }

  return axios.create({ baseURL });
};

export const kvkService = {
  /**
   * Search for companies in the Dutch Chamber of Commerce (KvK) database
   * @param {string} query - Search query (company name or KvK number)
   * @returns {Promise<Object>} Search results with companies
   */
  async searchCompanies(query) {
    try {
      if (!query || query.length < 3) {
        throw new Error('Query must be at least 3 characters long');
      }

      const api = await createApi();
      const response = await api.get('/public/kvk/search', {
        params: {
          q: query,
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error searching KvK companies:', error);

      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 400:
            throw new Error(data.error || 'Invalid search query');
          case 429:
            throw new Error('Too many requests. Please wait a moment before searching again.');
          case 500:
            throw new Error('Search service temporarily unavailable. Please try again later.');
          default:
            throw new Error(data.error || 'Failed to search companies');
        }
      } else if (error.request) {
        // Network error
        throw new Error('Network error. Please check your connection and try again.');
      } else {
        // Other error
        throw error;
      }
    }
  },

  /**
   * Get company details by KvK number
   * @param {string} kvkNumber - KvK number of the company
   * @returns {Promise<Object>} Company details
   */
  async getCompanyDetails(kvkNumber) {
    try {
      if (!kvkNumber) {
        throw new Error('KvK number is required');
      }

      const api = await createApi();
      const response = await api.get(`/public/kvk/company/${kvkNumber}`);

      return response.data;
    } catch (error) {
      console.error('Error fetching company details:', error);

      if (error.response?.status === 404) {
        throw new Error('Company not found');
      }

      throw new Error('Failed to fetch company details');
    }
  },

  /**
   * Enrich company profile data by vestigingsnummer
   * @param {string} vestigingsnummer - Vestigingsnummer of the company
   * @returns {Promise<Object>} Enriched company profile
   */
  async enrichCompanyProfile(vestigingsnummer) {
    try {
      if (!vestigingsnummer) {
        throw new Error('Vestigingsnummer is required');
      }

      const api = await createApi();
      const response = await api.get(`/public/kvk/vestiging/${vestigingsnummer}`);
      return response.data;
    } catch (error) {
      console.error('Error enriching company profile:', error);

      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 404:
            throw new Error('Company profile not found');
          case 429:
            throw new Error('Too many requests. Please wait a moment before trying again.');
          case 500:
            throw new Error('Profile service temporarily unavailable. Please try again later.');
          default:
            throw new Error(data.error || 'Failed to enrich company profile');
        }
      } else if (error.request) {
        throw new Error('Network error. Please check your connection and try again.');
      } else {
        throw error;
      }
    }
  },

  /**
   * Validate if a string is a valid KvK number format
   * @param {string} kvkNumber - KvK number to validate
   * @returns {boolean} True if format is valid
   */
  isValidKvkNumber(kvkNumber) {
    if (!kvkNumber || typeof kvkNumber !== 'string') {
      return false;
    }

    // KvK numbers are 8 digits
    const cleaned = kvkNumber.replace(/\D/g, '');
    return cleaned.length === 8 && /^\d{8}$/.test(cleaned);
  },

  /**
   * Format KvK number with proper spacing
   * @param {string} kvkNumber - KvK number to format
   * @returns {string} Formatted KvK number
   */
  formatKvkNumber(kvkNumber) {
    if (!kvkNumber) return '';

    const cleaned = kvkNumber.replace(/\D/g, '');
    if (cleaned.length === 8) {
      return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}`;
    }

    return kvkNumber;
  },
};
